import { useState } from "react";

import { Theme } from "@radix-ui/themes";
import "@radix-ui/themes/styles.css";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import "./App.css";
import { Toaster } from "./components/ui/sonner.jsx";
import { TooltipProvider } from "./components/ui/tooltip.jsx";
import { ApiProvider } from "./context/ApiContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConfirmProvider } from "./context/ConfirmContext";
import { DataProvider, useData } from "./context/DataContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import Activities from "./features/Activities";
import Attendance from "./features/Attendance";
import ContractorCosting from "./features/ContractorCosting";
import Contractors from "./features/Contractors";
import Dashboard from "./features/Dashboard";
import Invoices from "./features/Invoices";
import LeadManager from "./features/LeadManager";
import LoginPage from "./features/LoginPage";
import PasswordUpdatePage from "./features/LoginPage/PasswordUpdatePage";
import Materials from "./features/Materials";
import Meetings from "./features/Meetings";
import Navigation from "./features/Navigation";
import Payments from "./features/Payments";
import Photos from "./features/Photos";
import Projects from "./features/Projects";
import ProjectDetailPage from "./features/Projects/ProjectDetailPage";
import RoleManagement from "./features/RoleManagement";
import Tasks from "./features/Tasks";
import TenantManagement from "./features/TenantManagement";
import UserManagement from "./features/UserManagement";
import Vendors from "./features/Vendors";

// Protected Route Component
const ProtectedRoute = ({ children, tabId }) => {
  const { isAuthenticated, user } = useAuth();
  const userRole = user?.role?.name || user?.role;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Permission-based access control
  const hasAccess = (id) => {
    if (!id) return true;
    if (userRole === "super_admin") return true;

    const permissions = user?.permissions || {};
    return permissions[id]?.read === true;
  };

  if (!hasAccess(tabId)) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-yale-blue-700 mb-2">Access Denied</h2>
        <p className="text-yale-blue-500">You don't have permission to access this section.</p>
      </div>
    );
  }

  return children;
};

// Main App Component
function App() {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { projects, loading: dataLoading } = useData();
  const { theme, accentColor } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const loading = authLoading || (isAuthenticated && dataLoading && !projects.length);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center text-foreground">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Theme appearance={theme} accentColor={accentColor} panelBackground="translucent">
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/" replace />} />
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              user?.isPasswordChange === true || user?.is_password_change === true ? (
                <PasswordUpdatePage />
              ) : (
                <div className="app-container min-h-screen bg-background flex flex-col lg:flex-row transition-colors duration-300">
                  {/* Mobile sidebar overlay */}
                  {sidebarOpen && (
                    <div
                      className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-all duration-300"
                      onClick={() => setSidebarOpen(false)}
                    />
                  )}

                  <Navigation sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                  <main className="flex-1 lg:ml-64 w-full mobile-safe-content overflow-x-hidden flex flex-col">
                    <div className="p-4 md:p-6 lg:p-8 mx-auto w-full flex-1 flex flex-col animate-in fade-in duration-500">
                      <div className="flex-1 flex flex-col">
                        <Routes>
                          <Route
                            path="/"
                            element={
                              <ProtectedRoute tabId="dashboard">
                                <Dashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/roles"
                            element={
                              <ProtectedRoute tabId="role">
                                <RoleManagement />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/projects"
                            element={
                              <ProtectedRoute tabId="project">
                                <Projects />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/projects/:id"
                            element={
                              <ProtectedRoute tabId="project">
                                <ProjectDetailPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/tasks"
                            element={
                              <ProtectedRoute tabId="task">
                                <Tasks />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/activities"
                            element={
                              <ProtectedRoute tabId="activity">
                                <Activities />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/materials"
                            element={
                              <ProtectedRoute tabId="material">
                                <Materials />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/attendance"
                            element={
                              <ProtectedRoute tabId="attendance">
                                <Attendance />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/documents"
                            element={
                              <ProtectedRoute tabId="document">
                                <Photos />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/meetings"
                            element={
                              <ProtectedRoute tabId="meeting">
                                <Meetings />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/vendors"
                            element={
                              <ProtectedRoute tabId="vendor">
                                <Vendors />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/invoices"
                            element={
                              <ProtectedRoute tabId="invoice">
                                <Invoices />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/payments"
                            element={
                              <ProtectedRoute tabId="payment">
                                <Payments />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/contractors"
                            element={
                              <ProtectedRoute tabId="contractor">
                                <Contractors />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/contractor-costing"
                            element={
                              <ProtectedRoute tabId="costing">
                                <ContractorCosting />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/leads"
                            element={
                              <ProtectedRoute tabId="lead">
                                <LeadManager />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/users"
                            element={
                              <ProtectedRoute tabId="user">
                                <UserManagement />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/tenants"
                            element={
                              <ProtectedRoute tabId="tenant">
                                <TenantManagement />
                              </ProtectedRoute>
                            }
                          />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </div>
                    </div>
                  </main>
                </div>
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Theme>
  );
}

// App Wrapper with Auth Provider
const AppWithRouter = () => {
  return (
    <ThemeProvider>
      <ApiProvider>
        <AuthProvider>
          <DataProvider>
            <TooltipProvider>
              <Toaster position="top-right" closeButton />
              <ConfirmProvider>
                <App />
              </ConfirmProvider>
            </TooltipProvider>
          </DataProvider>
        </AuthProvider>
      </ApiProvider>
    </ThemeProvider>
  );
};

export default AppWithRouter;
